import { IGuernseyRow } from "src/components/shared/GuernseyMulti/Guernsey.types";
import type { ICol } from "src/interfaces";
import { v4 } from "uuid";

import { clearSingleValue } from "./clearValues";
/**
 * Clears the content of a Guernsey cell by resetting its rows and columns.
 *
 * @param cell - The cell object to be cleared, containing Guernsey-specific content.
 * @returns A new cell object with cleared content.
 */
const clearGuernsey = (cell: ICol) => {
  const newContent: any = {};
  Object.entries(cell.content as any).forEach(([key, row]) => {
    if (key === "cols") return;

    newContent[key] = (row as IGuernseyRow[]).map(item => {
      const newCols: any = {};
      Object.values(item.cols).forEach(col => {
        newCols[v4()] = { ...col, value: clearSingleValue(col), saved: false };
      });
      return { ...item, cols: newCols };
    });
  });
  return { ...cell, content: newContent, saved: false };
};
/**
 * Clears non-copyable properties from a cell object based on its attributes.
 *
 * @param cell - The cell object to be cleared.
 * @param dontClear - (Optional) A boolean indicating whether to skip clearing certain properties.
 * @returns A new cell object with cleared or modified properties.
 */
export const clearNonCopyable = (cell: any, dontClear?: boolean) => {
  try {
    if (cell.strikethrough) delete cell.strikethrough;
  } catch (e) {
    console.error(e);
  }

  try {
    if (cell.NMEAId) delete cell.NMEAId;
    if (cell.NMEAValue) delete cell.NMEAValue;
    // if (cell.NMEA) delete cell.NMEA;
  } catch (e) {
    console.error(e);
  }

  try {
    if (cell.source) delete cell.source;
  } catch (e) {
    console.error(e);
  }

  if (cell.unit === "24lock") {
    return { ...cell, value: { time: null, type: "" }, saved: false };
  } else if (cell.unit === "date" && cell.value && cell.value instanceof Date) {
    return { ...cell, value: cell.value.toISOString(), saved: false };
  } else if (cell.utcOnly && !cell.copy) {
    return {
      ...cell,
      value: {
        type: "UTC",
        time: null,
      },
      saved: false,
    };
  } else if (cell.unit === "time" && !cell.copy) {
    return {
      ...cell,
      value: {
        type: "LT",
        time: null,
      },
      saved: false,
    };
  } else if (Object.keys(cell).includes("copy") && !cell.copy && !dontClear) {
    return {
      ...cell,
      value: clearSingleValue(cell),
      saved: false,
    };
  } else if (cell.unit === "guernseyMulti") {
    return clearGuernsey(cell);
  } else {
    return { ...cell, saved: false };
  }
};
/**
 * Generates a new row object by clearing non-copyable properties from the provided columns.
 *
 * @param colsToConvert - An object containing the columns to be converted into a new row.
 * @param dontClear - (Optional) A boolean indicating whether to skip clearing certain properties.
 * @returns A new row object with cleared or modified columns.
 */
export const getNewRow = (
  colsToConvert: Record<string, ICol>,
  dontClear?: boolean,
) =>
  Object.entries(colsToConvert).reduce(
    (result: Record<string, ICol>, [, col]: any) => {
      result[v4()] = clearNonCopyable(col, dontClear);
      return result;
    },
    {},
  );
